import type { AsteroidKind } from '../entities';

export type AsteroidVariantConfig = {
  id: string;
  sizeClass: AsteroidKind;
  points: readonly (readonly [number, number])[];
  scale: number;
  speedMultiplier: number;
  rotationSpeedRange: { min: number; max: number };
  hitbox: { width: number; height: number; offsetX: number; offsetY: number };
  health: number;
  scoreOnDestroy: number;
  scoreOnPass: number;
  colors: { light: number; mid: number; shadow: number; accent: number };
  weight: number;
};

export const ASTEROID_SILHOUETTE_REVISION = 2;

export const ASTEROID_VARIANTS: readonly AsteroidVariantConfig[] = [
  {
    id: 'needle-shard',
    sizeClass: 'small',
    points: [
      [-11, -4],
      [-5, -7],
      [5, -6],
      [11, -2],
      [10, 4],
      [2, 7],
      [-8, 5],
      [-12, 1],
    ],
    scale: 0.82,
    speedMultiplier: 1.34,
    rotationSpeedRange: { min: -2.2, max: 2.2 },
    hitbox: { width: 17, height: 9, offsetX: 0, offsetY: 0 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0xb6a3b2, mid: 0x756382, shadow: 0x302943, accent: 0xdda86f },
    weight: 1.1,
  },
  {
    id: 'triangle-splinter',
    sizeClass: 'small',
    points: [
      [-9, 6],
      [-8, -3],
      [-3, -9],
      [5, -7],
      [10, -1],
      [12, 7],
      [3, 9],
      [-5, 8],
    ],
    scale: 0.86,
    speedMultiplier: 1.28,
    rotationSpeedRange: { min: -2.5, max: 2.5 },
    hitbox: { width: 15, height: 15, offsetX: 0, offsetY: 1 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0xa9b3c1, mid: 0x667184, shadow: 0x2e3445, accent: 0x55d9e8 },
    weight: 1,
  },
  {
    id: 'crooked-pentagon',
    sizeClass: 'medium',
    points: [
      [-14, -5],
      [-7, -12],
      [4, -14],
      [13, -8],
      [16, 3],
      [11, 11],
      [2, 15],
      [-12, 11],
      [-17, 2],
    ],
    scale: 0.95,
    speedMultiplier: 1.02,
    rotationSpeedRange: { min: -1.5, max: 1.5 },
    hitbox: { width: 26, height: 23, offsetX: 0, offsetY: 0 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0x9b8ca6, mid: 0x635474, shadow: 0x2a243c, accent: 0xc2a87a },
    weight: 1.2,
  },
  {
    id: 'bitten-rock',
    sizeClass: 'medium',
    points: [
      [-16, -8],
      [-6, -14],
      [8, -12],
      [15, -5],
      [17, 4],
      [11, 11],
      [2, 15],
      [-12, 10],
      [-18, 1],
    ],
    scale: 0.98,
    speedMultiplier: 0.98,
    rotationSpeedRange: { min: -1.3, max: 1.3 },
    hitbox: { width: 25, height: 24, offsetX: -1, offsetY: 0 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0xae927e, mid: 0x745a55, shadow: 0x342b39, accent: 0xd6bf83 },
    weight: 1.05,
  },
  {
    id: 'twin-lobe',
    sizeClass: 'medium',
    points: [
      [-18, -5],
      [-13, -12],
      [-3, -14],
      [7, -11],
      [16, -7],
      [19, 5],
      [11, 14],
      [1, 10],
      [-7, 15],
      [-16, 9],
    ],
    scale: 1,
    speedMultiplier: 0.93,
    rotationSpeedRange: { min: -1.1, max: 1.1 },
    hitbox: { width: 30, height: 23, offsetX: 0, offsetY: 0 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0x8fa0a4, mid: 0x56686e, shadow: 0x27343e, accent: 0x79c1ba },
    weight: 0.95,
  },
  {
    id: 'stone-slab',
    sizeClass: 'large',
    points: [
      [-25, -9],
      [-15, -15],
      [2, -17],
      [20, -12],
      [27, -3],
      [23, 8],
      [12, 13],
      [-4, 15],
      [-21, 11],
      [-28, 2],
    ],
    scale: 1,
    speedMultiplier: 0.8,
    rotationSpeedRange: { min: -0.75, max: 0.75 },
    hitbox: { width: 43, height: 23, offsetX: 0, offsetY: 0 },
    health: 2,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0x9d91a8, mid: 0x625770, shadow: 0x29243a, accent: 0xc7b4d4 },
    weight: 0.55,
  },
  {
    id: 'heavy-crown',
    sizeClass: 'large',
    points: [
      [-24, -9],
      [-18, -18],
      [-7, -22],
      [5, -20],
      [17, -15],
      [25, -7],
      [29, 2],
      [20, 19],
      [5, 24],
      [-10, 18],
      [-27, 9],
    ],
    scale: 0.96,
    speedMultiplier: 0.76,
    rotationSpeedRange: { min: -0.65, max: 0.65 },
    hitbox: { width: 42, height: 38, offsetX: 1, offsetY: 1 },
    health: 2,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0x9d877b, mid: 0x6b514c, shadow: 0x302735, accent: 0xe19b62 },
    weight: 0.48,
  },
  {
    id: 'crystal-fang',
    sizeClass: 'small',
    points: [
      [-8, 9],
      [-9, -2],
      [-4, -11],
      [4, -14],
      [10, -7],
      [13, 2],
      [7, 8],
      [3, 14],
      [-4, 13],
    ],
    scale: 0.9,
    speedMultiplier: 1.38,
    rotationSpeedRange: { min: -2.7, max: 2.7 },
    hitbox: { width: 12, height: 21, offsetX: 1, offsetY: 0 },
    health: 1,
    scoreOnDestroy: 2,
    scoreOnPass: 1,
    colors: { light: 0xa8d9d3, mid: 0x4f8391, shadow: 0x24364d, accent: 0x87f1e5 },
    weight: 0.9,
  },
] as const;
